import { px } from '@adsk/alloy-react-helpers';

import {
  AlloyLegacyThemeDesignTokens,
  RadioButtonDesignTokens,
} from '../types';
import { buildBoxShadow } from './common';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = WeaveTheme['tokens'];

export const getRadioButtonTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
  transitionDuration: number,
  transitionEasing: string,
): RadioButtonDesignTokens => ({
  container: {
    _: {
      display: 'block',
      position: 'relative',
      'border-style': 'solid',
      'border-radius': theme.borderRadius.circle,
      'border-color': theme.tokens.colors.border.value,
      cursor: 'pointer',
      '&:hover': {
        'box-shadow': theme.halos.hover,
      },
      '&:focus-within': {
        'box-shadow': theme.halos.focus,
      },
      '&:active': {
        'box-shadow': theme.halos.active,
        'border-color': theme.colors.charcoal500,
      },
      transition: `box-shadow ${transitionDuration}ms ${transitionEasing}, border-color ${transitionDuration}ms ${transitionEasing}`,
      height: px(16),
      width: px(16),
    },
    checked: {
      true: {
        'border-width': px(0),
      },
      false: {
        'border-width': px(1),
      },
    },
    disabled: {
      true: {
        cursor: 'not-allowed',
        'box-shadow': 'none',
        '&:hover': { 'box-shadow': 'none' },
        '&:focus-within': { 'box-shadow': 'none' },
        '&:active': {
          'box-shadow': 'none',
          'border-color': 'none',
        },
      },
      false: {},
    },
    compoundVariants: [
      {
        checked: 'false',
        disabled: 'true',
        styles: {
          'border-color': theme.tokens.colors.border.inactive.value,
        },
      },
    ],
  },
  shadow: {
    _: {
      'border-radius': theme.borderRadius.circle,
      width: '100%',
      height: '100%',
    },
    checked: {
      true: {
        'box-shadow': `inset 0 0 0 4px ${theme.tokens.colors.background.selected.value}`,
      },
      false: {},
    },
    disabled: {
      true: {},
      false: {},
    },
    compoundVariants: [
      {
        checked: 'true',
        disabled: 'true',
        styles: {
          'box-shadow': `inset 0 0 0 4px ${theme.tokens.colors.background.neutral.inactive.value}`,
        },
      },
    ],
  },
  input: {
    _: {
      cursor: 'inherit',
      position: 'absolute',
      opacity: 0,
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
      margin: 0,
      padding: 0,
      'z-index': '1',
    },
    checked: {
      true: {
        '&:before': {
          content: "' '",
          position: 'absolute',
          width: px(20),
          height: px(20),
          top: px(-2),
          left: px(-2),
        },
      },
      false: {
        '&:before': {
          content: "' '",
          position: 'absolute',
          width: px(20),
          height: px(20),
          top: px(-3),
          left: px(-3),
        },
      },
    },
    /**
     * pointer-events must be 'none' when disabled for a wrapping Tooltip to
     * disappear correctly.
     * @see https://github.com/react-component/tooltip/issues/18#issuecomment-411476678
     */
    disabled: {
      true: {
        'pointer-events': 'none',
      },
      false: {
        'pointer-events': 'auto',
      },
    },
  },
});

export const getRadioButtonTokensWeave = (
  tokens: WeaveTokens,
): RadioButtonDesignTokens => {
  const radio = tokens?.component?.radiobutton;

  const transitionProp = radio?.['transition-property']?.['box-shadow'];
  const transitionDur = radio?.['transition-duration'];
  const transitionFn = radio?.['transition-timing-function'];
  const transition =
    transitionProp && transitionDur && transitionFn
      ? `${transitionProp} ${transitionDur} ${transitionFn}`
      : undefined;

  return {
    container: {
      _: {
        display: 'block',
        position: 'relative',
        height: radio?.height,
        width: radio?.width,
        'border-style': radio?.['border-style'],
        'border-radius': radio?.['border-radius'],
        'border-color': radio?.['border-color']?.default,
        cursor: 'pointer',
        '&:hover': {
          'box-shadow': buildBoxShadow(radio, 'hover'),
        },
        '&:focus-within': {
          'box-shadow': `0px 0px 0px 0px white, ${buildBoxShadow(radio, 'focus')}`,
        },
        '&:active': {
          'box-shadow': buildBoxShadow(radio, 'pressed'),
        },
        transition,
      },
      checked: {
        true: {
          'border-color': radio?.['border-color']?.selected,
          'border-width': radio?.['border-width']?.selected,
          'border-style': radio?.['border-style'],
        },
        false: {
          'border-width': radio?.['border-width']?.default,
        },
      },
      disabled: {
        true: {
          cursor: 'not-allowed',
          opacity: radio?.opacity?.disabled,
          'box-shadow': 'none',
          '&:hover': { 'box-shadow': 'none' },
          '&:focus-within': { 'box-shadow': 'none' },
          '&:active': { 'box-shadow': 'none' },
        },
        false: {},
      },
    },
    input: {
      _: {
        cursor: 'inherit',
        position: 'absolute',
        opacity: 0,
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        margin: 0,
        padding: 0,
        'z-index': '1',
        '&:before': {
          content: "' '",
          position: 'absolute',
          width: radio?.container?.width,
          height: radio?.container?.height,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        },
      },
      checked: {
        true: {},
        false: {},
      },
      disabled: {
        true: {
          'pointer-events': 'none',
        },
        false: {
          'pointer-events': 'auto',
        },
      },
    },
  };
};
